import { IndicatorSeries, LineType, type IndicatorTemplate } from 'klinecharts';
import type { KLineData } from 'klinecharts';

interface PerLinesResult {
  ttmPer: number | null;
  growthPer: number | null;
  avgEpsPer: number | null;
}

// Type for regime data (quarter-specific EPS estimates)
export interface PerRegime {
  dayIndex: number; // floor(fiscal_period_end / 86400)
  ttmEps: number | null;
  growthEpsEstimate: number | null;
  avgEpsEstimate: number | null;
}

const PER_LINE_COLORS = ['#3B82F6', '#8B5CF6', '#EC4899'];

// Global storage for per-line regime data per symbol
declare global {
  interface Window {
    __per_lines_data?: Record<string, PerRegime[]>;
  }
}

export const perLines: IndicatorTemplate<PerLinesResult> = {
  name: 'PER_LINES',
  shortName: 'PER',
  series: IndicatorSeries.Normal,

  figures: [
    { key: 'ttmPer', title: 'TTM PER', type: 'line' },
    { key: 'growthPer', title: 'Growth PER', type: 'line' },
    { key: 'avgEpsPer', title: 'Avg EPS PER', type: 'line' },
  ],

  styles: {
    lines: PER_LINE_COLORS.map((color) => ({
      style: LineType.Solid,
      size: 1,
      color,
      smooth: false,
      dashedValue: [2, 2],
    })),
  },

  calc: (dataList: KLineData[]) => {
    // Retrieve symbol from window context (set by usePerLines hook)
    const currentSymbol = (window as unknown as { __per_lines_symbol: string }).__per_lines_symbol as string | undefined;
    if (!currentSymbol) {
      return dataList.map(() => ({
        ttmPer: null,
        growthPer: null,
        avgEpsPer: null,
      }));
    }

    const regimes = (window.__per_lines_data ?? {})[currentSymbol] ?? [];
    if (regimes.length === 0) {
      return dataList.map(() => ({
        ttmPer: null,
        growthPer: null,
        avgEpsPer: null,
      }));
    }

    return dataList.map((bar) => {
      // Convert bar timestamp (ms) to day index
      const barDayIndex = Math.floor(((bar.timestamp ?? 0) / 1000) / 86400);

      // Find latest regime where regimeDayIndex <= barDayIndex
      let activeRegime: PerRegime | null = null;
      for (let i = regimes.length - 1; i >= 0; i--) {
        if (regimes[i].dayIndex <= barDayIndex) {
          activeRegime = regimes[i];
          break;
        }
      }

      if (!activeRegime) {
        return {
          ttmPer: null,
          growthPer: null,
          avgEpsPer: null,
        };
      }

      // Compute PER values: close / denominator
      const close = bar.close ?? 0;

      const ttmPer = activeRegime.ttmEps !== null && activeRegime.ttmEps > 0
        ? close / activeRegime.ttmEps
        : null;

      const growthPer = activeRegime.growthEpsEstimate !== null && activeRegime.growthEpsEstimate > 0
        ? close / activeRegime.growthEpsEstimate
        : null;

      const avgEpsPer = activeRegime.avgEpsEstimate !== null && activeRegime.avgEpsEstimate > 0
        ? close / activeRegime.avgEpsEstimate
        : null;

      return { ttmPer, growthPer, avgEpsPer };
    });
  },

  createTooltipDataSource: () => ({
    name: 'PER Lines',
    calcParamsText: '',
    labels: ['TTM PER', 'Growth PER', 'Avg EPS PER'],
    values: [],
    icons: [],
  }),
};

export default perLines;
